import { ServiceArea, TeamServiceArea } from '@bringg/types';
import { action, observable, computed, toJS, makeObservable } from 'mobx';
import { isEqual } from 'lodash';
import moment from 'moment';
import { promiseMemoOne } from '@bringg-frontend/utils';

import ServiceAreasStore from 'bringg-web/stores/service-areas-store/service-areas-store';

export enum AREA_TYPE {
	ZIPCODES = 'SERVICE_AREA.ZIPCODES',
	COORDINATES = 'SERVICE_AREA.COORDINATES'
}

export enum FILTERS {
	BY_AREA_TYPE = 'byAreaType',
	BY_TEAMS = 'byTeams',
	BY_TEXT = 'byText'
}

export type FilterObj = {
	[FILTERS.BY_AREA_TYPE]: string;
	[FILTERS.BY_TEAMS]: number[] | null;
	[FILTERS.BY_TEXT]: string;
};

export enum USED_IN {
	DELIVERY_TERMS = 'SERVICE_AREA.DELIVERY_TERMS',
	AUTOMATION = 'SERVICE_AREA.AUTOMATION',
	COORDINATES = 'SERVICE_AREA.RATE_CARDS'
}

export type DecoratedServiceArea = Partial<
	ServiceArea & {
		assignedTeams: Partial<TeamServiceArea>[];
		center: google.maps.LatLng;
		areaType: AREA_TYPE;
		path: google.maps.LatLng[];
		pathBounds: google.maps.LatLngBounds;
		color: string;
	}
>;

const SERVICE_AREA_COLORS = ['#8e44ad', '#2980b9', '#27ae60', '#16a085', '#f39c12', '#d35400', '#c0392b'];
const SECONDARY_COLOR = '#7f8c8d';
const PADDING = 10;

function getColorById(id = 0) {
	return SERVICE_AREA_COLORS[id % SERVICE_AREA_COLORS.length];
}

export const NO_TEAM_OPTION_VALUE = -9999;

type GeoCache = {
	polygon: number[][];
	path: google.maps.LatLng[];
	pathBounds: google.maps.LatLngBounds;
	center: google.maps.LatLng;
};

export default class ServiceAreaViewStore {
	serviceAreasStore: ServiceAreasStore;
	map: google.maps.Map = null;
	focusedServiceArea: DecoratedServiceArea = null;
	initialized = false;
	filters: Partial<FilterObj> = {
		byAreaType: null,
		byTeams: [],
		byText: ''
	};
	latLngFF = false;

	// Cache for expensive geometry computations (path LatLng[], pathBounds, center).
	// Keyed by SA id.  Only recomputed when the polygon coordinate array changes.
	// Without this cache, every `decorated` recomputation recreates LatLng objects
	// for ALL polygons (~3M objects for 1000 SAs × 3000 dots), even if only a
	// color or focus changed.
	private _geoCache = new Map<number | undefined, GeoCache>();

	constructor() {
		makeObservable(this, {
			serviceAreasStore: observable,
			map: observable.ref,
			focusedServiceArea: observable,
			initialized: observable,
			focusServiceArea: action,
			withoutNewRecord: computed,
			allByName: computed,
			decorated: computed,
			bounds: computed,
			filters: observable.ref,
			filterBy: action,
			filtered: computed,
			init: action,
			save: action
		});
	}

	get filtered() {
		const { byText, byAreaType, byTeams } = this.filters;
		let filtered = this.decorated;

		// during creation id == null ignore filtering
		if (byText) {
			filtered = filtered.filter(serviceArea => {
				const hasName = () => serviceArea.name && serviceArea.name.toLowerCase().includes(byText.toLowerCase());
				const hasId = () =>
					serviceArea.id && serviceArea.id.toString().toLowerCase().includes(byText.toLowerCase());
				const hasExternalId = () =>
					serviceArea.external_id &&
					serviceArea.external_id.toString().toLowerCase().includes(byText.toLowerCase());
				return !serviceArea.id || hasName() || hasId() || hasExternalId();
			});
		}

		if (byAreaType) {
			filtered = filtered.filter(serviceArea => !serviceArea.id || serviceArea.areaType === byAreaType);
		}

		if (byTeams?.length) {
			filtered = filtered.filter(serviceArea => {
				return (
					!serviceArea.id ||
					(serviceArea.team_ids || []).some(teamId => byTeams.includes(teamId)) ||
					(!serviceArea.assignedTeams?.length && byTeams.includes(NO_TEAM_OPTION_VALUE))
				);
			});
		}

		return filtered;
	}

	get decorated(): DecoratedServiceArea[] {
		if (!this.serviceAreasStore) {
			return [];
		}

		const allSAs = this.getAll();
		const currentIds = new Set<number | undefined>();

		// When a team filter is active, only create geometry (LatLng objects) for
		// SAs that match the filter.  This avoids allocating millions of LatLng
		// objects for the entire merchant when the page only displays a handful
		// of team-specific service areas (e.g. teams page).
		const { byTeams } = this.filters;
		const hasTeamFilter = byTeams?.length > 0;
		const needsGeometryIds = new Set<number | undefined>();

		const results = allSAs.map(serviceArea => {
			currentIds.add(serviceArea.id);

			const needsGeometry =
				!hasTeamFilter ||
				!serviceArea.id || // new SA being created — always decorate
				(serviceArea.team_ids || []).some(id => byTeams.includes(id));

			if (needsGeometry) {
				needsGeometryIds.add(serviceArea.id);
			}

			// ── Geometry: use cache to avoid recreating millions of LatLng objects ──
			let path: google.maps.LatLng[] | undefined;
			let pathBounds: google.maps.LatLngBounds | null;
			let center: google.maps.LatLng | null;

			if (needsGeometry) {
				const cached = this._geoCache.get(serviceArea.id);

				if (cached && cached.polygon === serviceArea.polygon) {
					// Cache hit – polygon reference unchanged, reuse geometry
					path = cached.path;
					pathBounds = cached.pathBounds;
					center = cached.center;
				} else {
					// Cache miss – recompute geometry for this SA only
					path = serviceArea.polygon?.map(coords => {
						if (this.latLngFF) {
							return new google.maps.LatLng(coords[0], coords[1]);
						}
						return new google.maps.LatLng(coords[1], coords[0]);
					});
					pathBounds = path
						? path.reduce((bounds, position) => bounds.extend(position), new google.maps.LatLngBounds())
						: null;
					center = pathBounds ? pathBounds.getCenter() : null;

					this._geoCache.set(serviceArea.id, {
						polygon: serviceArea.polygon,
						path,
						pathBounds,
						center
					});
				}
			}
			// else: path/pathBounds/center remain undefined — SA is "lightweight"

			return {
				...serviceArea,
				assignedTeams: toJS(this.serviceAreasStore.teamsServiceAreas.get(serviceArea.id)) || [],
				center,
				areaType: serviceArea.polygon?.length ? AREA_TYPE.COORDINATES : AREA_TYPE.ZIPCODES,
				path,
				pathBounds,
				color: this.focusedServiceArea?.polygon
					? this.focusedServiceArea.id === serviceArea.id
						? getColorById(serviceArea.id)
						: SECONDARY_COLOR
					: getColorById(serviceArea.id)
			};
		});

		// Evict stale cache entries for SAs that were removed or no longer need geometry
		// (e.g. navigating from full SA page to team page frees non-team entries)
		for (const id of this._geoCache.keys()) {
			if (!currentIds.has(id) || !needsGeometryIds.has(id)) {
				this._geoCache.delete(id);
			}
		}

		return results;
	}

	getAll() {
		if (this.focusedServiceArea?.id) {
			return this.serviceAreasStore.getAll.map(sa => {
				if (sa.id === this.focusedServiceArea.id) {
					return this.focusedServiceArea;
				}

				return sa;
			});
		} else if (this.focusedServiceArea) {
			return [this.focusedServiceArea, ...this.serviceAreasStore.getAll];
		}

		return this.serviceAreasStore.getAll;
	}

	get allByName() {
		return _.keyBy(this.getAll(), 'name');
	}

	get withoutNewRecord() {
		return this.filtered.filter(sa => sa.id);
	}

	get bounds() {
		if (!this.filtered.length) {
			return null;
		}

		const bounds = new google.maps.LatLngBounds();

		const showFocused = Boolean(this.focusedServiceArea?.polygon);

		this.filtered
			.filter(sa => sa.path)
			.forEach(sa => {
				if (!showFocused || (showFocused && sa.id === this.focusedServiceArea.id)) {
					sa.path.forEach(coordinates => bounds.extend(coordinates));
				}
			});

		return bounds;
	}

	focusServiceArea(serviceArea: DecoratedServiceArea) {
		this.focusedServiceArea = serviceArea ? { ...serviceArea } : null;
	}

	onMapLoad(map) {
		this.map = map;
	}

	centerMap() {
		if (!this.map) {
			return;
		}

		this.map.fitBounds(this.bounds, PADDING);
	}

	filterBy(filterObj: Partial<FilterObj>) {
		this.filters = filterObj;
	}

	async save(serviceArea: Partial<ServiceArea>, assignedTeams: Partial<TeamServiceArea>[], { teamId = null } = {}) {
		let result;
		let teamsServiceAreas = assignedTeams;

		if (serviceArea.id) {
			result = await this.serviceAreasStore.update(serviceArea);
		} else {
			if (teamId) {
				teamsServiceAreas = [
					{
						team_id: teamId,
						priority: 0,
						effective_start_time: moment().startOf('day').toISOString()
					}
				];
			}

			result = await this.serviceAreasStore.create(serviceArea);
		}

		const anyChanges = !isEqual(this.serviceAreasStore.teamsServiceAreas.get(result.id), teamsServiceAreas);

		if (anyChanges) {
			await this.serviceAreasStore.updateAllTeamsServiceAreas(result.id, teamsServiceAreas);
		}

		return result;
	}

	async init(serviceAreasStore: ServiceAreasStore, latLngFF = false) {
		if (!this.serviceAreasStore) {
			this.serviceAreasStore = serviceAreasStore;
		}
		this.latLngFF = latLngFF;

		return this._doInit();
	}

	private _doInit = promiseMemoOne(async () => {
		await this.serviceAreasStore.fetchAll();
		this.initialized = true;
	});
}
